import { useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ProtocolLinkContext } from "@/contexts/ProtocolLinkContext";

const LinkedText = ({ text, className = "" }) => {
  const { protocols, currentProtocol } = useContext(ProtocolLinkContext);
  const location = useLocation();
  
  const linkedContent = useMemo(() => {
    if (!text || !protocols || protocols.length === 0) return text?.replace?.(/\*{2,3}/g, '') || text;
    
    const protocolMap = new Map();

    // Only link to protocols within the same service as the current protocol
    const currentService = currentProtocol?.service || "ground";
    const sameServiceProtocols = protocols.filter(p => (p.service || "ground") === currentService);
    
    const keywordMappings = {
      'combative': 'anxious-combative',
      'anxious': 'anxious-combative',
      'psychological': 'anxious-combative',
      'agitated': 'anxious-combative',
      'tor': 'death-in-field',
      'termination of resuscitation': 'death-in-field',
      'hypotension': 'shock-hypotension',
      'hypotensive': 'shock-hypotension',
      'hypovolemia': 'shock-hypotension',
      'hypovolemic': 'shock-hypotension',
      'chest decompression': 'tension-pneumothorax',
      'needle decompression': 'tension-pneumothorax',
      'ett': 'difficult-airway-management',
      'rsi': 'rsi-checklist',
      'ett +/- rsi': 'difficult-airway-management',
      'intubation': 'difficult-airway-management',
      'intubation/ rsi checklist': 'rsi-checklist',
      'intubation checklist': 'rsi-checklist',
      'advanced airway': 'difficult-airway-management',
      'difficult airway management': 'difficult-airway-management',
      'difficult airway': 'difficult-airway-management',
      'sedation': 'procedural-sedation',
      'procedural sedation': 'procedural-sedation',
      'antiemetics': 'nausea-vomiting',
      'antiemetic': 'nausea-vomiting',
      'pediatric arrest': 'pediatric-cardiac-arrest',
      'pediatric cardiac arrest': 'pediatric-cardiac-arrest',
      'pediatric seizures': 'pediatric-seizures',
      'pediatric seizure': 'pediatric-seizures',
      'pediatric hypoglycemia': 'pediatric-hypoglycemia',
      'pediatric shock': 'pediatric-shock-hypotension',
      'pediatric rosc': 'pediatric-rosc',
      'pediatric respiratory': 'pediatric-respiratory-distress',
      'pediatric bradycardia': 'pediatric-bradycardia',
      'pediatric tachycardia': 'pediatric-tachycardia',
      'pediatric procedural sedation': 'pediatric-procedural-sedation',
      'pediatric advanced airway': 'pediatric-advanced-airway',
      'pediatric foreign body': 'pediatric-fbao',
      'pediatric systematic approach': 'pediatric-systematic-approach',
      'neonatal resuscitation': 'neonatal-resuscitation',
      'nrp': 'neonatal-resuscitation',
      'cardiac arrest': 'cardiac-arrest',
      'acute coronary syndrome': 'acute-coronary-syndrome',
      'acs': 'acute-coronary-syndrome',
      'rosc': 'rosc',
      'return of spontaneous circulation': 'rosc',
      'post-cardiac arrest': 'rosc',
      'postpartum hemorrhage': 'postpartum-hemorrhage',
      'pre-eclampsia': 'preeclampsia-eclampsia',
      'eclampsia': 'preeclampsia-eclampsia',
      'dnr': 'no-patient-transport',
      'advanced directives': 'dnr-advanced-directives',
      'pain control': 'pain-control',
      'anaphylaxis': 'anaphylaxis',
      'anaphylactic shock': 'anaphylactic-shock',
      'sepsis': 'sepsis',
      'cardiogenic shock': 'cardiogenic-shock',
      'stroke': 'stroke',
      'hypoglycemia': 'hypoglycemia',
      'seizure': 'seizures',
      'status epilepticus': 'seizures',
      'narcotic overdose': 'narcotic-od',
      'opioid overdose': 'narcotic-od',
      'burns': 'burns',
      'hypothermia': 'hypothermia',
      'frostbite': 'frostbite',
      'head trauma': 'head-trauma',
    };
    
    const procedureMappings = {
      'quick clot': 'hemostatic-dressing',
      'hemostatic': 'hemostatic-dressing',
      'cpap': 'cpap',
      'continuous positive airway pressure': 'cpap',
      'io access': 'io-therapy',
      'intraosseous': 'io-therapy',
      'iv access': 'iv-therapy',
      'intravenous': 'iv-therapy',
      'transcutaneous pacing': 'transcutaneous-pacing',
      'synchronized cardioversion': 'synchronized-cardioversion',
      'cardioversion': 'synchronized-cardioversion',
      'king lt': 'king-lts-d',
      'king lts': 'king-lts-d',
      'etco2': 'etco2-monitoring',
      'end tidal co2': 'etco2-monitoring',
    };
    
    const medicationMappings = {
      'ntg': 'nitroglycerin',
      'nitroglycerin': 'nitroglycerin',
      'epinephrine': 'epinephrine',
      'atropine': 'atropine',
      'amiodarone': 'amiodarone',
      'adenosine': 'adenosine',
      'midazolam': 'midazolam',
      'ketamine': 'ketamine',
      'fentanyl': 'fentanyl',
      'morphine': 'morphine',
      'naloxone': 'naloxone',
      'dextrose': 'dextrose',
      'glucagon': 'glucagon',
      'diphenhydramine': 'diphenhydramine',
      'salbutamol': 'salbutamol',
      'ipratropium': 'ipratropium',
      'magnesium sulfate': 'magnesium-sulfate',
      'ondansetron': 'ondansetron',
      'dimenhydrinate': 'dimenhydrinate',
      'gravol': 'dimenhydrinate',
      'haloperidol': 'haloperidol',
      'lorazepam': 'lorazepam',
      'diazepam': 'diazepam',
      'dopamine': 'dopamine',
      'norepinephrine': 'norepinephrine',
      'oxytocin': 'oxytocin',
      'tranexamic acid': 'tranexamic-acid',
      'txa': 'tranexamic-acid',
      'acetaminophen': 'acetaminophen',
      'tylenol': 'acetaminophen',
      'ibuprofen': 'ibuprofen',
      'ketorolac': 'ketorolac',
      'toradol': 'ketorolac',
      'dexamethasone': 'dexamethasone',
      'succinylcholine': 'succinylcholine',
      'sodium bicarbonate': 'sodium-bicarbonate',
      'nahco3': 'sodium-bicarbonate',
      'calcium gluconate': 'calcium-gluconate',
      'ca gluconate': 'calcium-gluconate',
      'tetracaine': 'tetracaine',
    };
    
    sameServiceProtocols.forEach(p => {
      if (p.id === currentProtocol?.id) return;
      
      protocolMap.set(p.name.toLowerCase(), { ...p, linkType: 'protocol' });
      
      const nameWithoutParens = p.name.replace(/\s*\([^)]*\)\s*/g, '').trim();
      if (nameWithoutParens !== p.name && nameWithoutParens.length > 3) {
        protocolMap.set(nameWithoutParens.toLowerCase(), { ...p, linkType: 'protocol' });
      }
      
      const abbrevMatch = p.name.match(/\(([A-Z]{2,}[^)]*)\)/);
      if (abbrevMatch && abbrevMatch[1]) {
        protocolMap.set(abbrevMatch[1].toLowerCase(), { ...p, linkType: 'protocol' });
      }
      
      const commonPatterns = [
        p.name.replace(/ protocol$/i, '').toLowerCase(),
        p.name.replace(/^see /i, '').toLowerCase(),
      ];
      commonPatterns.forEach(pattern => {
        if (pattern !== p.name.toLowerCase() && pattern.length > 3) {
          protocolMap.set(pattern, { ...p, linkType: 'protocol' });
        }
      });
      
      Object.entries(keywordMappings).forEach(([keyword, protocolId]) => {
        if (p.id === protocolId && currentProtocol?.id !== protocolId) {
          protocolMap.set(keyword, { ...p, linkType: 'protocol' });
        }
      });
    });
    
    Object.entries(procedureMappings).forEach(([keyword, procedureId]) => {
      protocolMap.set(keyword, { id: procedureId, linkType: 'procedure' });
    });
    
    Object.entries(medicationMappings).forEach(([keyword, medId]) => {
      protocolMap.set(keyword, { id: medId, linkType: 'medication' });
    });
    
    const cleanText = text.replace(/\*{2,3}/g, '');
    const sortedNames = Array.from(protocolMap.keys()).sort((a, b) => b.length - a.length);
    
    if (sortedNames.length === 0) return cleanText;
    
    const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = sortedNames.map(name => `(${escapeRegex(name)})`).join('|');
    const regex = new RegExp(pattern, 'gi');
    
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(cleanText)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: cleanText.slice(lastIndex, match.index) });
      }
      
      const matchedText = match[0];
      const item = protocolMap.get(matchedText.toLowerCase());
      
      if (item) {
        parts.push({ 
          type: 'link', 
          content: matchedText, 
          item: item,
          key: `${item.id}-${match.index}`
        });
      } else {
        parts.push({ type: 'text', content: matchedText });
      }
      
      lastIndex = regex.lastIndex;
    }
    
    if (lastIndex < cleanText.length) {
      parts.push({ type: 'text', content: cleanText.slice(lastIndex) });
    }
    
    return parts.length > 0 ? parts : cleanText;
  }, [text, protocols, currentProtocol]);
  
  if (typeof linkedContent === 'string') {
    const cleaned = linkedContent.replace(/\*{2,3}/g, '');
    return <span className={className}>{cleaned}</span>;
  }
  
  return (
    <span className={className}>
      {linkedContent.map((part, idx) => {
        if (part.type === 'link') {
          const item = part.item;
          let linkPath = '';
          let linkClass = 'text-blue-600 dark:text-primary hover:underline font-medium';
          
          if (item.linkType === 'procedure') {
            linkPath = `/procedure/${item.id}`;
            linkClass = 'text-green-600 dark:text-green-400 hover:underline font-medium';
          } else if (item.linkType === 'medication') {
            linkPath = `/medications#${item.id}`;
            linkClass = 'text-amber-600 dark:text-yellow-400 hover:underline font-medium';
          } else {
            linkPath = `/protocol/${item.id}`;
          }
          
          return (
            <Link
              key={part.key || idx}
              to={linkPath}
              state={{ fromProtocol: currentProtocol }}
              className={linkClass}
              data-testid={`link-${item.id}`}
            >
              {part.content}
            </Link>
          );
        }
        return <span key={idx}>{part.content}</span>;
      })}
    </span>
  );
};

export default LinkedText;
